import {
  ComponentProps,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { FieldPath, FieldValues, PathValue } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { useOnBeforeUnloadPrompt } from '@/hooks/useOnBeforeUnloadPrompt';
import { useRegisterSlideNavDirtyGetter } from '@/modules/presentation/clientPresentation/context/SlideNavGuard.context';

import { DividerSlide } from '../dividerSlide/DividerSlide';
import { useSlideFieldLiveDirty } from './useSlideFieldLiveDirty';

export interface FormAwareDividerSlideProps<TShape extends FieldValues> {
  Slide?: ComponentProps<typeof DividerSlide>['Slide'];
  dividerCopy: string;
  customContent: string;
  /** Form path the divider title commits to. */
  dividerTitlePath: FieldPath<TShape>;
  /** Form path the body content commits to. */
  customContentPath: FieldPath<TShape>;
  slideId?: string;
  footer?: ReactNode;
  highlightVariables?: boolean;
  /**
   * Reports whether the slide has in-progress (pre-blur) edits. Lets consumers
   * (the template editor) confirm before discarding mid-keystroke edits that
   * haven't yet committed to the form.
   */
  onUnsavedEditsChange?: (hasUnsavedEdits: boolean) => void;
}

/**
 * Form-aware {@link DividerSlide}: binds the title/body fields of any form
 * (`TShape`) to the slide given their field paths. Divider slides have no
 * prompt mode. See {@link FormAwareCoverSlide} for the shared commit +
 * live-dirty behavior.
 */
export function FormAwareDividerSlide<TShape extends FieldValues>({
  Slide,
  dividerCopy,
  customContent,
  dividerTitlePath,
  customContentPath,
  slideId,
  footer,
  highlightVariables,
  onUnsavedEditsChange,
}: FormAwareDividerSlideProps<TShape>) {
  const { setValue } = useFormContext<TShape>();

  const commit = useCallback(
    (path: FieldPath<TShape>, value: string) => {
      // The caller types these paths to the slide's string text fields
      // (title/content), but TS can't prove the value type for a generic
      // TShape, so assert it. Not a path cast — the paths themselves are checked
      // at the call site.
      setValue(path, value as PathValue<TShape, typeof path>, {
        shouldDirty: true,
        shouldTouch: true,
      });
    },
    [setValue]
  );

  const {
    isDirty: titleDirty,
    onLiveChange: onTitleLiveChange,
    reset: resetTitle,
  } = useSlideFieldLiveDirty(dividerCopy);
  const {
    isDirty: bodyDirty,
    onLiveChange: onBodyLiveChange,
    reset: resetBody,
  } = useSlideFieldLiveDirty(customContent);
  const hasUnsavedEdits = titleDirty || bodyDirty;
  // Browser-level navigation (refresh, close, history).
  useOnBeforeUnloadPrompt(hasUnsavedEdits);
  // Surface live-dirty so consumers can confirm before discarding even
  // mid-keystroke, before the editor blurs and commits to the form.
  useEffect(() => {
    onUnsavedEditsChange?.(hasUnsavedEdits);
    return () => onUnsavedEditsChange?.(false);
  }, [hasUnsavedEdits, onUnsavedEditsChange]);
  // Remounts the slide on discard (see handleDiscard) so the editors re-seed
  // their DOM from the committed form values.
  const [revertKey, setRevertKey] = useState(0);
  const handleDiscard = useCallback(() => {
    // Clear the live-dirty tracking AND remount the editors: they own their DOM
    // content uncontrolled, so without a remount a later blur could commit the
    // abandoned text even after the user chose "Discard changes".
    resetTitle();
    resetBody();
    setRevertKey((k) => k + 1);
  }, [resetTitle, resetBody]);
  // In-app slide-to-slide navigation in the presentation designer. No-op in the
  // template editor, which has no SlideNavGuard provider.
  useRegisterSlideNavDirtyGetter(hasUnsavedEdits, handleDiscard);

  const onChange = useCallback(
    (value: string) => commit(dividerTitlePath, value),
    [commit, dividerTitlePath]
  );

  const onCustomContentChange = useCallback(
    (value: string) => commit(customContentPath, value),
    [commit, customContentPath]
  );

  return (
    <DividerSlide
      key={revertKey}
      Slide={Slide}
      dividerCopy={dividerCopy}
      customContent={customContent}
      slideId={slideId}
      footer={footer}
      onChange={onChange}
      onCustomContentChange={onCustomContentChange}
      onInput={onTitleLiveChange}
      onCustomContentInput={onBodyLiveChange}
      highlightVariables={highlightVariables}
    />
  );
}
